import { Room, RoomEvent, Track, type LocalTrack, type Participant } from 'livekit-client';
import type { LiveKitCredentials } from '../shared/types';
import { get } from './api';
import { selectCameras, type CameraInfo } from './callDevices';
import { chat } from './store';

export interface CallTile {
  id: string;
  name: string;
  local: boolean;
  screen?: boolean;
  video?: Track;
  speaking?: boolean;
}

export type CallStatus = 'idle' | 'connecting' | 'connected' | 'error';

function collectTiles(room: Room): CallTile[] {
  const tiles: CallTile[] = [];
  const push = (participant: Participant, local: boolean) => {
    const camera = participant.getTrackPublication(Track.Source.Camera);
    const screen = participant.getTrackPublication(Track.Source.ScreenShare);
    const name = participant.name || participant.identity;
    if (screen?.track && !screen.isMuted) {
      tiles.push({ id: `${participant.identity}-screen`, name, local, screen: true, video: screen.track });
    }
    tiles.push({
      id: participant.identity,
      name,
      local,
      video: camera?.track,
      speaking: participant.isSpeaking,
    });
  };
  if (room.localParticipant) push(room.localParticipant, true);
  for (const participant of room.remoteParticipants.values()) push(participant, false);
  return tiles;
}

/** LiveKit session for one conversation; mirrors the old `useCall` hook. */
export class CallController {
  status = $state<CallStatus>('idle');
  error = $state<string | null>(null);
  tiles = $state.raw<CallTile[]>([]);
  mic = $state(true);
  camera = $state(false);
  cameras = $state.raw<CameraInfo[]>([]);
  canSwitchCamera = $state(false);
  activeCameraId = $state<string | null>(null);
  switchingCamera = $state(false);
  screen = $state(false);

  #room: Room | null = null;
  #conversationId: string;

  constructor(conversationId: string) {
    this.#conversationId = conversationId;
  }

  get connected(): boolean {
    return this.status === 'connected';
  }

  #refresh(room: Room) {
    this.tiles = collectTiles(room);
    this.mic = room.localParticipant.isMicrophoneEnabled;
    this.camera = room.localParticipant.isCameraEnabled;
    this.screen = room.localParticipant.isScreenShareEnabled;
  }

  // Labels only become available after camera permission, so this is refreshed
  // on join, on publish and after every switch. A single-camera device simply
  // reports canSwitchCamera=false and the UI disables switching.
  async #refreshCameras() {
    const room = this.#room;
    if (!navigator.mediaDevices?.enumerateDevices) {
      this.cameras = [];
      this.canSwitchCamera = false;
      this.activeCameraId = null;
      return;
    }
    try {
      const devices = await Room.getLocalDevices('videoinput', false);
      const selection = selectCameras(devices, room?.getActiveDevice('videoinput'));
      this.cameras = selection.cameras;
      this.canSwitchCamera = selection.canSwitch;
      this.activeCameraId = selection.activeId;
    } catch {
      this.cameras = [];
      this.canSwitchCamera = false;
      this.activeCameraId = null;
    }
  }

  async leave() {
    const room = this.#room;
    this.#room = null;
    if (room) await room.disconnect().catch(() => {});
    this.status = 'idle';
    this.tiles = [];
    this.screen = false;
    this.camera = false;
    this.cameras = [];
    this.canSwitchCamera = false;
    this.activeCameraId = null;
    this.switchingCamera = false;
  }

  async join() {
    const conversationId = this.#conversationId;
    if (!conversationId || this.#room) return;
    this.status = 'connecting';
    this.error = null;
    const me = chat.getState().me;
    try {
      const identity = me.id.replace(/[^a-zA-Z0-9_-]/g, '-');
      const credentials = await get<LiveKitCredentials>(
        `/api/livekit/token?conversationId=${encodeURIComponent(conversationId)}` +
          `&identity=${encodeURIComponent(identity)}` +
          `&name=${encodeURIComponent(me.name || '')}`,
      );
      const room = new Room({ adaptiveStream: true, dynacast: true });
      this.#room = room;

      const update = () => this.#refresh(room);
      room
        .on(RoomEvent.ParticipantConnected, update)
        .on(RoomEvent.ParticipantDisconnected, update)
        .on(RoomEvent.TrackSubscribed, update)
        .on(RoomEvent.TrackUnsubscribed, update)
        .on(RoomEvent.LocalTrackPublished, () => {
          update();
          void this.#refreshCameras();
        })
        .on(RoomEvent.LocalTrackUnpublished, update)
        .on(RoomEvent.TrackMuted, update)
        .on(RoomEvent.TrackUnmuted, update)
        .on(RoomEvent.ActiveSpeakersChanged, update)
        .on(RoomEvent.Disconnected, () => {
          this.#room = null;
          this.status = 'idle';
          this.tiles = [];
        });

      await room.connect(credentials.url, credentials.token);
      this.status = 'connected';
      this.#refresh(room);
      void this.#refreshCameras();
      // A blocked mic prompt must not abort an already-joined call.
      try {
        await room.localParticipant.setMicrophoneEnabled(true);
        this.mic = true;
      } catch (cause) {
        this.mic = false;
        chat.notify(`Microphone unavailable: ${(cause as Error).message}`, 'error');
      }
    } catch (cause) {
      await this.#room?.disconnect().catch(() => {});
      this.#room = null;
      this.status = 'error';
      this.error = (cause as Error).message;
    }
  }

  async toggleMic() {
    const room = this.#room;
    if (!room) return;
    const next = !room.localParticipant.isMicrophoneEnabled;
    try {
      await room.localParticipant.setMicrophoneEnabled(next);
      this.mic = next;
    } catch (cause) {
      this.mic = false;
      chat.notify(`Microphone unavailable: ${(cause as Error).message}`, 'error');
    }
  }

  async toggleCamera() {
    const room = this.#room;
    if (!room) return;
    const next = !room.localParticipant.isCameraEnabled;
    try {
      await room.localParticipant.setCameraEnabled(next);
      this.camera = next;
      if (next) await this.#refreshCameras();
    } catch (cause) {
      chat.notify(`Camera unavailable: ${(cause as Error).message}`, 'error');
    }
    this.#refresh(room);
  }

  /**
   * Switches the active camera without touching audio or rejoining. LiveKit
   * replaces the local video track; a restart fallback covers SDK versions
   * where switchActiveDevice returns false.
   */
  async switchCamera() {
    const room = this.#room;
    if (!room || this.switchingCamera) return;
    const selection = selectCameras(this.cameras, room.getActiveDevice('videoinput'));
    if (!selection.canSwitch || !selection.nextId) {
      chat.notify('Only one camera is available on this device', 'info');
      return;
    }
    this.switchingCamera = true;
    try {
      const switched = await room.switchActiveDevice('videoinput', selection.nextId);
      if (!switched) {
        const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
        const track = publication?.track as LocalTrack | undefined;
        if (!track || typeof track.restartTrack !== 'function') throw new Error('The active camera could not be replaced');
        await track.restartTrack({ deviceId: selection.nextId });
      }
      this.activeCameraId = selection.nextId;
      await this.#refreshCameras();
      this.#refresh(room);
    } catch (error) {
      chat.notify(`Camera switch failed: ${(error as Error).message}`, 'error');
    } finally {
      this.switchingCamera = false;
    }
  }

  async toggleScreen() {
    const room = this.#room;
    if (!room) return;
    const next = !room.localParticipant.isScreenShareEnabled;
    await room.localParticipant.setScreenShareEnabled(next).catch(() => {});
    this.screen = next;
    this.#refresh(room);
  }

  async destroy() {
    const room = this.#room;
    this.#room = null;
    await room?.disconnect().catch(() => {});
  }
}
